// BookingPage: Customer info form before payment confirmation
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import BookingForm from "../components/BookingForm";
import { useBooking } from "../context/BookingContext";
import { useSettings } from "../context/SettingsContext";
import AlertMessage from "../components/AlertMessage";

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTour, travelers } = useBooking();
  const { settings } = useSettings();
  const [status, setStatus] = useState({ type: null, msg: "" });

  // Discount calculation
  const cs = settings.currency_symbol || "$";
  const discount = Number(settings.discount_percentage) || 0;
  const originalPrice = selectedTour?.price || 0;
  const discountedPrice = discount > 0 ? originalPrice * (1 - discount / 100) : originalPrice;
  const totalPrice = discountedPrice * travelers;

  // Check if returning from confirm page to edit
  const initialData = location.state?.bookingData;

  // Handler - navigate to confirmation page with booking data
  const handleContinue = (payload) => {
    navigate("/booking/confirm", {
      state: { bookingData: payload }
    });
  };

  // Calculer la date de fin (start_date + duration_days - 1)
  const calculateEndDate = () => {
    if (!selectedTour?.start_date || !selectedTour?.durationDays) return null;

    const startDate = new Date(selectedTour.start_date);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + selectedTour.durationDays - 1);

    return endDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {/* FORM */}
      <div className="md:col-span-2">
        <h1 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">Finalize Booking</h1>

        {status.type && (
          <div className="mb-4">
            <AlertMessage
              type={status.type}
              onClose={() => setStatus({ type: null, msg: "" })}
            >
              {status.msg}
            </AlertMessage>
          </div>
        )}

        {!selectedTour ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center dark:border-slate-800 dark:bg-slate-900">
            <p className="mb-4 text-slate-600 dark:text-slate-300">
              No tour selected. Please choose a tour first.
            </p>
            <Link
              to="/tours"
              className="inline-block rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white transition-colors duration-300 hover:bg-emerald-700"
            >
              Browse Tours
            </Link>
          </div>
        ) : (
          <>
            <BookingForm
              onSubmit={handleContinue}
              tour={selectedTour}
              initialData={initialData}
            />
          </>
        )}
      </div>

      {/* RÉCAPITULATIF */}
      <aside className="h-fit space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold">Booking Summary</h2>

        {!selectedTour ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No tour selected
          </p>
        ) : (
          <div className="space-y-3">
            {/* Image */}
            <img
              src={selectedTour.thumbnail || "/placeholder-tour.jpg"}
              alt={selectedTour.title}
              className="h-40 w-full rounded-lg object-cover"
            />

            {/* Titre */}
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {selectedTour.title}
            </div>

            {/* Infos de base */}
            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center gap-2">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>{selectedTour.city}</span>
              </div>

              <div className="flex items-center gap-2">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{selectedTour.durationDays} days</span>
              </div>

              {/* DATES (start_date et end_date) */}
              {selectedTour.start_date && (
                <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
                  <div className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                    Tour Dates
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>
                      {new Date(selectedTour.start_date).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        }
                      )}
                      {calculateEndDate() && <> → {calculateEndDate()}</>}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Prix */}
            <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
              <div className="flex items-baseline gap-2">
                {discount > 0 ? (
                  <>
                    <span className="text-lg line-through text-slate-400">
                      {cs}{originalPrice}
                    </span>
                    <span className="text-2xl font-bold text-emerald-600">
                      {cs}{discountedPrice.toFixed(2)}
                    </span>
                    <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full font-semibold">
                      -{discount}%
                    </span>
                  </>
                ) : (
                  <span className="text-2xl font-bold text-emerald-600">
                    {cs}{originalPrice}
                  </span>
                )}
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  per person
                </span>
              </div>
              {travelers > 1 && (
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  {travelers} travelers × {cs}{discountedPrice.toFixed(2)} =
                  <span className="font-semibold text-emerald-600"> {cs}{totalPrice.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Trust badges */}
            <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
              <div className="space-y-2 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Free cancellation up to 48h before</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Instant confirmation</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>24/7 customer support</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
